/*
 * Storefront ecommerce events. Checkout events are deliberately excluded:
 * the Shopify Web Pixel owned by babalu-data-platform is their sole producer.
 */
(function () {
  'use strict';

  var node = document.getElementById('babalu-ecommerce-data');
  if (!node) return;

  var source;
  try {
    source = JSON.parse(node.textContent);
  } catch (error) {
    console.warn('[Babalu analytics] Invalid Liquid data contract.', error);
    return;
  }

  var REQUIRED_FIELDS = ['item_id', 'item_sku', 'item_color', 'item_size', 'item_barcode'];

  function text(value) {
    return value === undefined || value === null ? '' : String(value).trim();
  }

  function optionValue(names, values, matcher) {
    for (var index = 0; index < (names || []).length; index += 1) {
      if (matcher.test(text(names[index]).toLowerCase())) return text(values[index]);
    }
    return '';
  }

  function namedOption(options, matcher) {
    for (var index = 0; index < (options || []).length; index += 1) {
      if (matcher.test(text(options[index].name).toLowerCase())) return text(options[index].value);
    }
    return '';
  }

  function productItem(variant, quantity) {
    var product = source.product || {};
    var taxonomy = product.taxonomy || {};
    var color = optionValue(product.option_names, variant.options, /(^|[ _-])colou?r($|[ _-])/);
    var size = optionValue(product.option_names, variant.options, /(^|[ _-])(talla|size)($|[ _-])/);
    return {
      item_id: text(variant.barcode),
      item_name: text(product.title),
      item_brand: text(taxonomy.brand || product.vendor),
      item_category: text(taxonomy.department || product.type),
      item_category2: text(taxonomy.category),
      item_category3: text(taxonomy.type),
      item_category4: text(taxonomy.product_type),
      item_collection: text(taxonomy.collection),
      item_product: text(taxonomy.product),
      item_line: text(taxonomy.line),
      item_variant: text(variant.title),
      price: Number(variant.price),
      quantity: Number(quantity || 1),
      item_sku: text(variant.sku),
      item_color: color,
      item_size: size,
      item_barcode: text(variant.barcode),
      shopify_variant_id: text(variant.id)
    };
  }

  function cartItem(line, quantityOverride) {
    var taxonomy = line.taxonomy || {};
    var color = namedOption(line.options, /(^|[ _-])colou?r($|[ _-])/);
    var size = namedOption(line.options, /(^|[ _-])(talla|size)($|[ _-])/);
    var quantity = quantityOverride === undefined ? line.quantity : quantityOverride;
    return {
      item_id: text(line.barcode),
      item_name: text(line.title),
      item_brand: text(taxonomy.brand || line.vendor),
      item_category: text(taxonomy.department || line.type),
      item_category2: text(taxonomy.category),
      item_category3: text(taxonomy.type),
      item_category4: text(taxonomy.product_type),
      item_collection: text(taxonomy.collection),
      item_product: text(taxonomy.product),
      item_line: text(taxonomy.line),
      item_variant: text(line.variant_title),
      price: Number(line.price),
      quantity: Number(quantity || 1),
      discount: Number(line.discount || 0),
      item_sku: text(line.sku),
      item_color: color,
      item_size: size,
      item_barcode: text(line.barcode),
      shopify_variant_id: text(line.id)
    };
  }

  function isEligible(item) {
    return REQUIRED_FIELDS.every(function (field) { return Boolean(text(item[field])); });
  }

  function push(eventName, ecommerce, extra) {
    if (!ecommerce.items.every(isEligible)) {
      console.warn('[Babalu analytics] Event not sent: required variant identity is incomplete.', eventName, ecommerce.items);
      return false;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(Object.assign({ event: eventName, ecommerce: ecommerce }, extra));
    return true;
  }

  function emitViewCartFromLines(lines, value, extra) {
    var items = (lines || []).map(function (line) { return cartItem(line); });
    if (!items.length) return;
    push('view_cart', { currency: source.currency, value: Number(value), items: items }, extra);
  }

  function cartLinesToMap(lines) {
    var map = Object.create(null);
    (lines || []).forEach(function (line) {
      var key = text(line.id);
      if (!key) return;
      map[key] = { line: line, quantity: Number(line.quantity) || 0 };
    });
    return map;
  }

  /*
   * Fresh cart data, taxonomy-enriched (includes variant.barcode), fetched
   * via Shopify's Section Rendering API against sections/babalu-cart-data
   * .liquid. `cart` is a global Liquid object available on every page, so
   * this resolves correctly no matter which page triggers it.
   *
   * Needed because the raw JSON response from /cart/add.js,
   * /cart/change.js, /cart/update.js and /cart/clear.js never includes
   * variant.barcode — our item identity contract requires it, and the
   * page-load snapshot in `source.cart` goes stale the moment the cart
   * drawer changes anything without a full navigation.
   */
  function fetchFreshCart() {
    return fetch('/?sections=babalu-cart-data', { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        var html = data && data['babalu-cart-data'];
        if (!html || typeof DOMParser === 'undefined') return null;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var dataNode = parsed.getElementById('babalu-cart-data');
        if (!dataNode) return null;
        try {
          return JSON.parse(dataNode.textContent);
        } catch (error) {
          return null;
        }
      })
      .catch(function () { return null; });
  }

  /*
   * Pure diff between two cart states: which lines lost quantity (→
   * remove_from_cart) and which gained quantity (→ add_to_cart), each
   * carrying only the delta, not the new total. No side effects, so it's
   * exercised directly in tests without mocking fetch/DOM.
   */
  function diffCartLines(previousMap, nextMap) {
    var removals = [];
    var additions = [];

    Object.keys(previousMap).forEach(function (key) {
      var before = previousMap[key];
      var afterQty = nextMap[key] ? nextMap[key].quantity : 0;
      if (afterQty >= before.quantity) return;
      removals.push(cartItem(before.line, before.quantity - afterQty));
    });

    Object.keys(nextMap).forEach(function (key) {
      var after = nextMap[key];
      var beforeQty = previousMap[key] ? previousMap[key].quantity : 0;
      if (after.quantity <= beforeQty) return;
      additions.push(cartItem(after.line, after.quantity - beforeQty));
    });

    return { removals: removals, additions: additions };
  }

  /*
   * The cart drawer (sections/cart-drawer.liquid, id="CartDrawer") is a
   * native <dialog> rendered globally via the system-group section group
   * — present on every page, opened by the theme's own (minified, not
   * ours to touch) core JS via showModal(). Rather than hooking into
   * that, watch the dialog's own `toggle` event: it fires on every
   * open-state change regardless of what triggered it, so this works
   * whether the drawer opened after a cart mutation or from a plain
   * "view cart" click with no mutation at all.
   *
   * `view_cart` is pixel-owned per EVENT-CONTRACT.md, and the pixel
   * already covers it correctly for a real /cart page visit (confirmed
   * live with real production data). The drawer is a different story:
   * it's a pure client-side toggle with no page navigation, and Shopify's
   * Web Pixel does not detect `cart_viewed` from it — confirmed live
   * 2026-09-23 two ways: opening a full drawer produced zero pixel-sourced
   * view_cart events, and even adding an explicit `fetch('/cart.js')`
   * (confirmed firing via the browser's own network log) still produced
   * none 10+ minutes later. Whatever Shopify's pixel actually keys off of
   * for `cart_viewed`, it isn't a same-origin fetch to that endpoint.
   *
   * So the drawer case is a deliberate, documented exception to
   * EVENT-CONTRACT.md (see ADR-006 addendum 2026-09-23): GTM owns
   * `view_cart` ONLY when it originates here, tagged `cart_view_source:
   * "drawer"`. The real /cart page-load path that used to push an
   * untagged `view_cart` here (via `emitViewCart()`) was removed
   * 2026-09-24 (BACKLOG data-platform-081) — the pixel already owns that
   * case per EVENT-CONTRACT.md and no GTM trigger read the untagged push
   * anymore since 076's dedup cleanup, so it was dead weight. This drawer
   * path is untouched and still the only caller of
   * `emitViewCartFromLines`.
   */
  function watchCartDrawer() {
    var dialog = document.getElementById('CartDrawer');
    if (!dialog || typeof dialog.addEventListener !== 'function') return;
    dialog.addEventListener('toggle', function () {
      if (!dialog.open) return;
      fetchFreshCart().then(function (fresh) {
        if (!fresh) return;
        emitViewCartFromLines(fresh.items, fresh.value, { cart_view_source: 'drawer' });
      });
    });
  }

  watchCartDrawer();

  window.BabaluEcommerceAnalytics = {
    buildProductItem: productItem,
    buildCartItem: cartItem,
    isEligible: isEligible,
    push: push,
    cartLinesToMap: cartLinesToMap,
    diffCartLines: diffCartLines
  };
}());
